import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { V2_COLORS } from './tokens';
import { V2Nav, V2Footer, V2Kicker, V2_REVEAL as REVEAL, PrimaryCTA } from './V2Shared';
import { useDocumentMeta } from './useDocumentMeta';
import './v2.css';

const fraunces = () => ({ fontFamily: 'Fraunces', fontStyle: 'italic', fontWeight: 300, fontOpticalSizing: 'auto' });

/* ---------------- ABOUT ---------------- */
export function AboutPage() {
  useDocumentMeta(
    'About · BrightLabs — Small team. Serious capability.',
    'A modern digital agency with a deliberately small roster. Creative, technology and growth under one team. Fewer clients, deeper work, aggressive standards.',
    '/about'
  );
  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }} data-testid="v2-about">
      <V2Nav scheme="dark" active="about" />

      <section className="pt-40 pb-16 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <V2Kicker num="—">About</V2Kicker>
        <motion.h1 {...REVEAL} className="v2-display mt-6 max-w-[16ch] leading-[0.9]">
          Small team.
          <br />
          <em style={fraunces()}>Serious</em> capability.
        </motion.h1>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.15 }} className="v2-lede mt-10 max-w-[64ch] text-[#B8B4A6]">
          BrightLabs is a modern digital agency that combines creative, technology and growth. We build websites, brands and custom software — and then run the marketing, ads and automation that pulls customers through them. All from one team, on one invoice.
        </motion.p>
      </section>

      <section className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <div className="grid md:grid-cols-2 gap-14">
          <div>
            <V2Kicker num="01" warm>How we think</V2Kicker>
            <h2 className="v2-h1 mt-6 max-w-[18ch]">Fewer clients, <em style={fraunces()}>deeper</em> work.</h2>
            <p className="v2-lede mt-6 text-[#B8B4A6] max-w-[52ch]">
              We&rsquo;d rather have a small roster of clients who trust us with real work than a big roster we can&rsquo;t give proper attention to. It&rsquo;s the reason our clients stay for years, not months.
            </p>
          </div>
          <div>
            <V2Kicker num="02" warm>How we work</V2Kicker>
            <h2 className="v2-h1 mt-6 max-w-[18ch]">Modern <em style={fraunces()}>tooling</em>, aggressive standards.</h2>
            <p className="v2-lede mt-6 text-[#B8B4A6] max-w-[52ch]">
              Modern stack, AI in the workflow, custom internal tooling. Five people can ship what fifteen used to — the efficiency shows up in the pace of delivery and the calibre of the output, not the invoice.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-black/[0.08] py-24 md:py-32" style={{ background: V2_COLORS.paperCream, color: V2_COLORS.ink }}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
          <V2Kicker num="03" warm>The family</V2Kicker>
          <h2 className="v2-h1 mt-6 max-w-[18ch]" style={{ color: V2_COLORS.ink }}>
            One <em style={fraunces()}>umbrella</em>. Three brands.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              { n: 'BrightLabs', p: 'The growth agency you&rsquo;re on now. Creative, technology and growth for ambitious businesses — wherever they are.' },
              { n: 'Brightridge', p: 'Specialist recruitment for UK regulated care — with a live learning platform (Pathways) and AI Care Interview Coach.' },
              { n: 'Bright EOR',  p: 'Employer-of-record so growing businesses can hire brilliant people across borders without setting up entities everywhere.' },
            ].map((b) => (
              <div key={b.n} className="p-6 rounded-sm ring-1 ring-black/10 bg-white/50">
                <div className="v2-h3 text-[20px]" style={{ color: V2_COLORS.ink }}>{b.n}</div>
                <p className="mt-3 text-[14px] leading-relaxed text-[#3F3A2E]" dangerouslySetInnerHTML={{ __html: b.p }} />
              </div>
            ))}
          </div>
          <p className="mt-10 v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] max-w-[80ch]">
            Some brands we&rsquo;ve built or supported — including Cobra Thai and MacroBeast — are separately-owned businesses run by the founder, not group companies. We&rsquo;ve simply provided the digital, brand and technology work behind them.
          </p>
        </div>
      </section>

      <section className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <V2Kicker num="04">How we work</V2Kicker>
        <div className="mt-8 grid md:grid-cols-3 gap-8">
          {[
            { h: 'Remote-native', p: 'A tight, senior team that works with brands wherever they&rsquo;re based — same standards, same senior operators, timezone-friendly.' },
            { h: 'Selective roster', p: 'We deliberately keep a small client list so every engagement gets founder-level attention rather than being handed to an account exec.' },
            { h: 'Bespoke scoping', p: 'No off-the-shelf packages on the main site. Every engagement is scoped after a proper conversation about what actually needs building.' },
          ].map((l) => (
            <div key={l.h} className="border-t border-white/[0.08] pt-6">
              <div className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A]">The approach</div>
              <div className="v2-h2 mt-2 text-white text-[26px]">{l.h}</div>
              <p className="mt-3 text-[14px] text-[#B8B4A6] leading-relaxed" dangerouslySetInnerHTML={{ __html: l.p }} />
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.08] py-32 md:py-48 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <motion.h2 {...REVEAL} className="v2-display leading-[0.9] max-w-[16ch]">
          Tell us the <em style={fraunces()}>outcome</em>.
        </motion.h2>
        <motion.div {...REVEAL} className="mt-12"><PrimaryCTA>Start a Project</PrimaryCTA></motion.div>
      </section>
      <V2Footer />
    </div>
  );
}

/* ---------------- SERVICES INDEX ---------------- */
const SERVICES = {
  websites: {
    slug: 'websites', name: 'Websites & Digital Experiences', num: 'i',
    hero: { title: 'Websites that actually move businesses forward.', italic: 'forward' },
    lede: 'Strategy, UX, design, development, conversion. Websites written like real editorial and built like real software.',
    from: 'From £1,200',
    accent: '#3B4DFF',
    what: ['Strategy + information architecture','UX & design','Development (React, Next.js, Framer)','Performance & Core Web Vitals','Conversion architecture','CRM & marketing integrations','Analytics + attribution','Ongoing support &amp; iteration'],
    for: 'Ambitious businesses whose current site is under-selling them — or brands launching properly for the first time.',
    outcome: 'A digital experience that clients, prospects and Google all respect.',
  },
  creative: {
    slug: 'creative-social', name: 'Creative, Social & Branding', num: 'ii',
    hero: { title: 'We don\'t manage feeds. We build brands worth following.', italic: 'worth following' },
    lede: 'Brand identity, creative direction, storytelling and social strategy — the content system that keeps the flywheel turning. This is also where our Branding & Identity work lives (logos, guidelines, brand books).',
    from: 'From £650/mo',
    accent: '#E85D2F',
    what: ['Branding & identity (logo, palette, type, guidelines)','Creative direction','Brand storytelling','Social strategy','Short-form video & reels','Carousels & static','Campaign creative','Paid-media creative'],
    for: 'Brands who&rsquo;ve outgrown &ldquo;12 posts a month&rdquo; and want ongoing creative that actually shifts perception.',
    outcome: 'A brand that people follow because they want to — not because they were forced to.',
  },
  'paid-growth': {
    slug: 'paid-growth', name: 'Paid Growth · Performance', num: 'iii',
    hero: { title: 'We don\'t run ads for clicks. We run them for growth.', italic: 'growth' },
    lede: 'Meta, Google, retargeting, landing pages, tracking. One system — not four hand-offs.',
    from: 'From £499/mo + ad spend',
    accent: '#34D399',
    what: ['Meta Ads (Facebook &amp; Instagram)','Google Ads (Search &amp; Performance Max)','Retargeting funnels','Landing-page optimisation','Conversion tracking (server-side ready)','Creative testing loops','Attribution setup','Monthly performance reviews'],
    for: 'Businesses ready to spend intentionally — not businesses looking for a &ldquo;boost&rdquo;.',
    outcome: 'CPL, CAC and ROAS numbers you actually trust. Traffic that pays.',
  },
  seo: {
    slug: 'seo', name: 'SEO & Organic Growth', num: 'iv',
    hero: { title: 'The traffic that keeps working when the ads switch off.', italic: 'switch off' },
    lede: 'Technical, on-page, content, local SEO — done with the discipline of a compounding investment.',
    from: 'From £500/mo · 3-month min',
    accent: '#7B3DFF',
    what: ['Technical SEO audits','On-page optimisation','Content strategy &amp; writing','Local SEO (Google Business Profile)','Rankings + traffic dashboards','Backlink strategy','Schema &amp; structured data','Core Web Vitals tuning'],
    for: 'Businesses in it for the long game — where every month you don&rsquo;t do this is a month a competitor pulls further ahead.',
    outcome: 'Page-1 rankings that hold, traffic that compounds, and enquiries that keep landing while you sleep.',
  },
  software: {
    slug: 'software', name: 'Software, Apps & Automation', num: 'v',
    hero: { title: 'We build the systems beneath the marketing.', italic: 'beneath' },
    lede: 'Custom CRMs, applications, dashboards, PWAs and AI-driven workflows. The reason our clients ship faster than their competitors.',
    from: 'Projects from £1,800',
    accent: '#C939FF',
    what: ['Custom CRM platforms','Business applications &amp; SaaS','Customer portals &amp; dashboards','PWAs &amp; mobile experiences','AI workflow automation','Complex API integrations','Internal ops platforms','Data models &amp; migrations'],
    for: 'Businesses whose growth is being held back by spreadsheets, off-the-shelf tools that don&rsquo;t fit, or manual work that shouldn&rsquo;t exist.',
    outcome: 'A platform your team runs on — not a stack of tools you fight with every day.',
  },
  'growth-partnership': {
    slug: 'growth-partnership', name: 'Growth Partnership', num: 'vi',
    hero: { title: 'Your digital team, without building one internally.', italic: 'internally' },
    lede: 'Creative, social, web, paid, SEO, CRM, automation and strategy under one monthly engagement. Quarterly plans, monthly delivery, weekly ops.',
    from: 'From £1,650/mo',
    accent: '#E85D2F',
    what: ['Quarterly strategy planning','Monthly delivery across disciplines','Weekly ops + comms','Creative + content pipeline','Paid growth + SEO','CRM + automation','Dashboards + attribution','Direct access to senior team'],
    for: 'Businesses ready to invest in growth without hiring in-house — or ones outgrowing their current agency mix.',
    outcome: 'One team accountable for the whole growth engine. One invoice. One relationship.',
  },
};

export function ServicesIndex() {
  useDocumentMeta(
    'Services · BrightLabs — Build · Create · Grow · Automate',
    'Websites & Digital, Creative & Social, Paid Growth, SEO, Software & Automation, and Growth Partnership. One team across all disciplines. From £499/mo.',
    '/services'
  );
  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }} data-testid="v2-services-index">
      <V2Nav scheme="dark" active="services" />
      <section className="pt-40 pb-14 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <V2Kicker num="—">Services</V2Kicker>
        <motion.h1 {...REVEAL} className="v2-display mt-6 max-w-[18ch] leading-[0.9]">
          Four disciplines.
          <br />
          <em style={fraunces()}>Six</em> ways to work with us.
        </motion.h1>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.15 }} className="v2-lede mt-10 max-w-[62ch] text-[#B8B4A6]">
          Build. Create. Grow. Automate. Pick the discipline that fits — or start with a discovery and let us tell you what actually needs building. Every engagement scoped bespoke after a proper conversation.
        </motion.p>
      </section>

      <section className="border-t border-white/[0.08]">
        {Object.values(SERVICES).map((s, i) => (
          <motion.div key={s.slug} {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.05 + i * 0.04 }}>
            <Link to={`/services/${s.slug}`} data-testid={`v2-services-${s.slug}`} className="group block px-6 md:px-12 lg:px-20 py-10 md:py-14 border-b border-white/[0.08] hover:bg-white/[0.02] transition-colors">
              <div className="max-w-[1600px] mx-auto grid md:grid-cols-[80px_1.4fr_1fr_auto] gap-6 md:gap-10 items-baseline">
                <div className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A]">{s.num}</div>
                <div className="v2-h1 text-[36px] md:text-[52px]" style={{ color: V2_COLORS.paper }}>
                  <em style={{ ...fraunces(), color: s.accent }}>{s.name.split(' ')[0]}</em>{' '}
                  <span>{s.name.split(' ').slice(1).join(' ')}</span>
                </div>
                <div className="flex flex-col items-start md:items-end gap-0.5" style={{ color: s.accent }}>
                  <span className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A]">From</span>
                  <span className="text-[26px] md:text-[30px] leading-none" style={{ ...fraunces(), color: s.accent }}>
                    {s.from.replace(/^From\s+/i, '')}
                  </span>
                </div>
                <ArrowUpRight size={26} className="text-[#9C978A] group-hover:text-white group-hover:rotate-45 transition-all duration-500" />
              </div>
            </Link>
          </motion.div>
        ))}
      </section>
      <V2Footer />
    </div>
  );
}

/* ---------------- SERVICES DETAIL ---------------- */
export function ServiceDetail() {
  const { slug } = useParams();
  const s = Object.values(SERVICES).find((x) => x.slug === slug) || SERVICES.websites;
  useDocumentMeta(
    `${s.name} · BrightLabs`,
    s.lede,
    `/services/${s.slug}`
  );

  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }} data-testid={`v2-service-${s.slug}`}>
      <V2Nav scheme="dark" active="services" />
      <section className="pt-40 pb-16 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <Link to="/services" className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A] hover:text-white transition-colors">← All services</Link>
        <div className="mt-10 v2-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: s.accent }}>{s.num} · {s.name}</div>
        <motion.h1 {...REVEAL} className="v2-display mt-6 max-w-[16ch] leading-[0.9]">
          {s.hero.title.split(s.hero.italic)[0]}
          <em style={{ ...fraunces(), color: s.accent }}>{s.hero.italic}</em>
          {s.hero.title.split(s.hero.italic)[1] || ''}
        </motion.h1>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.15 }} className="v2-lede mt-10 max-w-[60ch] text-[#B8B4A6]">{s.lede}</motion.p>
        <motion.div {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.25 }} className="mt-10 flex items-baseline gap-3">
          <span className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A]">From</span>
          <span className="text-[36px] md:text-[44px] leading-none" style={{ ...fraunces(), color: s.accent }}>
            {s.from.replace(/^From\s+/i, '')}
          </span>
        </motion.div>
      </section>

      <section className="border-t border-white/[0.08] py-24 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto grid md:grid-cols-2 gap-14">
        <div>
          <V2Kicker num="01" warm>What&rsquo;s included</V2Kicker>
          <ul className="mt-8 space-y-3">
            {s.what.map((w) => (
              <li key={w} className="flex items-start gap-3 text-[16px] text-[#CFCBBD] border-b border-white/[0.06] pb-3">
                <Check size={14} style={{ color: s.accent }} className="mt-1.5 shrink-0" />
                <span dangerouslySetInnerHTML={{ __html: w }} />
              </li>
            ))}
          </ul>
        </div>
        <div>
          <V2Kicker num="02" warm>Who this is for</V2Kicker>
          <p className="v2-lede text-[#CFCBBD] mt-8 text-[19px] leading-relaxed" dangerouslySetInnerHTML={{ __html: s.for }} />
          <V2Kicker num="03" warm className="mt-16 block">The outcome</V2Kicker>
          <p className="v2-h3 mt-6 text-white text-[24px] leading-snug max-w-[38ch]">
            <em style={fraunces()}>{s.outcome}</em>
          </p>
        </div>
      </section>

      <section className="border-t border-white/[0.08] py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <motion.h2 {...REVEAL} className="v2-display leading-[0.9] max-w-[16ch]">Ready to <em style={fraunces()}>talk</em>?</motion.h2>
        <div className="mt-12 flex gap-3 flex-wrap">
          <PrimaryCTA>Start a Project</PrimaryCTA>
        </div>
      </section>
      <V2Footer />
    </div>
  );
}

export { SERVICES };
